import { MoneyBags } from '@creditornot/cb-icons';
import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React, { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { KycIdentificationOption, KycIdentificationProvider } from 'apis/legitimizer/types';
import { Typography as T } from 'common/typography';
import { MENU_MOBILE_BREAKPOINT } from 'modules/menu/constants';

import CustomRadioCard from './CustomRadioCard';
import AudkenniAppcIcon from '../icons/AudkenniAppIcon';
import IDCardIcon from '../icons/IDCardIcon';
import MobileIDIcon from '../icons/MobileIDIcon';
import SmartIDIcon from '../icons/SmartIDIcon';

type Props = {
  options: KycIdentificationOption[];
  selectedOption: KycIdentificationOption;
  handleSelectAuthOption: (option: string) => void;
};

const Root = styled.section`
  width: 100%;
`;

type ProviderConfig = {
  icon: ReactNode;
  labelId: string;
};

type IconProps = {
  checked: boolean;
};

const Heading = styled(T.Heading2)`
  font-size: 20px;
  line-height: 24px;
  text-align: center;
`;

const OptionsWrapper = styled.div`
  margin-block-start: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 8px;

  @media (min-width: ${MENU_MOBILE_BREAKPOINT}px) {
    flex-direction: row;
  }
`;

const iconAttributes = {
  height: 48,
  width: 48,
  'aria-hidden': true,
};

const BankIdIcon = styled(MoneyBags).attrs({
  ...iconAttributes,
})<IconProps>`
  fill: ${props => (props.checked ? colors.wolt100 : cssThemedValue('textSecondary'))};
  margin-block-end: 8px;
`;

// TODO: This icon is used as a placeholder until we can add the correct icon to cb-icons
// The correct icon would be an ID document symbol
const CardIcon = styled(IDCardIcon).attrs({
  ...iconAttributes,
})<IconProps>`
  fill: ${props => (props.checked ? colors.wolt100 : cssThemedValue('textSecondary'))};
  margin-block-end: 8px;
`;

// TODO: This icon is used as a placeholder until we can add the correct icon to cb-icons
const MobileIcon = styled(MobileIDIcon).attrs({
  ...iconAttributes,
})<IconProps>`
  fill: ${props => (props.checked ? colors.wolt100 : cssThemedValue('textSecondary'))};
  margin-block-end: 8px;
`;

// TODO: This icon is used as a placeholder until we can add the correct icon to cb-icons
const SmartIdIcon = styled(SmartIDIcon).attrs({
  ...iconAttributes,
})<IconProps>`
  fill: ${props => (props.checked ? colors.wolt100 : cssThemedValue('textSecondary'))};
  margin-block-end: 8px;
`;

const AudkenniIcon = styled(AudkenniAppcIcon).attrs({
  ...iconAttributes,
})<IconProps>`
  fill: ${props => (props.checked ? colors.wolt100 : cssThemedValue('textSecondary'))};
  margin-block-end: 8px;
`;

const getProviderConfig = (provider: KycIdentificationProvider, checked: boolean): ProviderConfig | null => {
  switch (provider) {
    case 'ftn-auth':
    case 'bankid':
      return {
        icon: <BankIdIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.bankid',
      };
    case 'smartid':
    case 'smartid-lt':
    case 'smartid-lv':
    case 'smartid-retail-lv':
      return {
        icon: <SmartIdIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.smartid',
      };
    case 'esteid':
      return {
        icon: <CardIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.esteid',
      };
    case 'esteid-mobiil':
      return {
        icon: <MobileIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.mobileid',
      };
    case 'mitid':
      return {
        icon: <CardIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.mitid',
      };
    case 'nbid':
      return {
        icon: <CardIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.nbid',
      };
    case 'nbid-mobil':
      return {
        icon: <MobileIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.mobileid',
      };
    case 'sbid':
      return {
        icon: <BankIdIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.sbid',
      };
    case 'onfido':
      return {
        icon: <CardIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.onfido',
      };
    case 'mobile':
      return {
        icon: <MobileIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.mobileid',
      };
    case 'audkenni_app':
      return {
        icon: <AudkenniIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.audkenni',
      };
    case 'smartcard':
      return {
        icon: <CardIcon checked={checked} />,
        labelId: 'compliance.kyc.providers.smartcard',
      };
    default:
      return null;
  }
};

const IdentityVerificationOptions = ({ options, selectedOption, handleSelectAuthOption }: Props) => {
  const handleSelectMethod = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = event;
    handleSelectAuthOption(value);
  };

  const isOptionSupported = (option: KycIdentificationOption): boolean => !!getProviderConfig(option.provider, false);

  return (
    <Root>
      <Heading>
        <FormattedMessage id="compliance.kyc.intro.choose" />
      </Heading>
      <OptionsWrapper>
        {options.filter(isOptionSupported).map(option => {
          const config = getProviderConfig(option.provider, option.provider === selectedOption.provider);
          return (
            <CustomRadioCard
              checked={option.provider === selectedOption.provider}
              name="identity-verification-option"
              handleChange={handleSelectMethod}
              value={option.provider}
              key={option.provider}
              icon={config?.icon}
              labelId={config?.labelId}
            />
          );
        })}
      </OptionsWrapper>
    </Root>
  );
};

export default IdentityVerificationOptions;
